import { DeleteOutlined, Send, StopCircle } from '@mui/icons-material';
import {
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useMutation } from '@tanstack/react-query';
import { isNullOrUndefined, isPlainObject } from '@umatch/utils';
import { stringify } from '@umatch/utils/object';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';

const useStyles = makeStyles({
  button: { marginBottom: '1rem', marginTop: '1rem' },
  cellX5: { minWidth: '5rem' },
  cellX9: { minWidth: '9rem' },
  cellX20: { minWidth: '20rem' },
  conditions: { textAlign: 'left', whiteSpace: 'pre' },
  table: {
    marginBottom: '2rem',
  },
  tableContainer: {
    maxHeight: props => props.maxHeight, // Use the maxHeight from props
  },
  stickyHeader: {
    position: 'sticky',
    top: 0,
    backgroundColor: '#fff', // Ensure the background color matches the table
    zIndex: 1,
  },
});

const statusDictionary = {
  pending: 'pendente',
  failed: 'falhou',
  sent: 'enviado',
  timeout: 'timeout',
  'in progress': 'em progresso',
};

function formatDateForMui(date) {
  return DateTime.fromISO(date).toFormat("yyyy-MM-dd'T'HH:mm");
}

export function NotificationCalendar() {
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [maxHeight, setMaxHeight] = useState(window.innerHeight - 200); // Initial maxHeight

  useEffect(() => {
    const handleResize = () => {
      setMaxHeight(window.innerHeight - 200); // Adjust the value as needed
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    (async () => {
      const {
        data: { data, perPage, total },
      } = await api.get(`/admin/notifications/page/${page + 1}`);
      const transformed = data.map(row =>
        Object.fromEntries(
          Object.entries(row).map(([key, value]) => {
            if (key === 'created_at' || key === 'end_time') {
              return [key, value ? new Date(value).toLocaleString() : '-'];
            }
            if (key === 'start_time') {
              return [key, value ? formatDateForMui(value) : null];
            }
            if (key === 'conditions') {
              return [key, isPlainObject(value) ? stringify(value, { length: 40 }) : '-'];
            }
            if (key === 'number_sent') {
              return [key, new Intl.NumberFormat('pt-br').format(value)];
            }
            if (key === 'status') {
              return [key, statusDictionary[value] || value];
            }
            if (key === 'with_nina') {
              return [key, Boolean(value)];
            }
            if (isNullOrUndefined(value) || value === '') {
              return [key, '-'];
            }
            return [key, value];
          })
        )
      );
      setRows(transformed);
      setRowsPerPage(perPage);
      setTotalRows(total);
    })();
  }, [page]);
  const classes = useStyles({ maxHeight });

  const handleEdit = (id, field, value) => {
    const newRows = rows.map(row => {
      if (row.id === id) {
        return { ...row, [field]: value };
      }
      return row;
    });
    setRows(newRows);
  };

  const updateNotification = useMutation(
    async row => {
      const { id, message, image_url, start_time, title } = row;
      await api.put(`/admin/notifications/${id}`, {
        message,
        image_url,
        start_time: start_time ? new Date(start_time).toISOString() : null,
        title,
      });
    },
    {
      onSuccess: () => toast('Notificação atualizada com sucesso!', { type: 'success' }),
      onError: error => {
        toast('Erro ao notificar atualização!', { type: 'error' });
        console.error(error);
      },
    }
  );

  const stopNotification = useMutation(
    async row => {
      const { id } = row;
      await api.get(`/admin/notifications/${id}/stop`);
    },
    {
      onSuccess: () => toast('Notificação parada com sucesso!', { type: 'success' }),
      onError: error => {
        toast('Erro ao parar disparos da atualização!', { type: 'error' });
        console.error(error);
      },
    }
  );

  const deleteNotification = useMutation(
    async id => {
      await api.delete(`/admin/notifications/${id}`);
    },
    {
      onSuccess: () => toast('Notificação deletada com sucesso!', { type: 'success' }),
      onError: error => {
        toast('Erro ao deletar notificação!', { type: 'error' });
        console.error(error);
      },
      onSettled: id => {
        setRows(rows.filter(row => row.id !== id));
        setTotalRows(prev => prev - 1);
      },
    }
  );

  const handleSubmit = async id => {
    if (window.confirm('Tem certeza que quer atualizar essa notificação?')) {
      const row = rows.find(r => r.id === id);
      updateNotification.mutate(row);
    }
  };

  const handleStop = async id => {
    if (window.confirm('Tem certeza que quer parar os disparos dessa notificação?')) {
      const row = rows.find(r => r.id === id);
      stopNotification.mutate(row);
    }
  };

  const handleDelete = id => {
    if (window.confirm('Tem certeza que quer deletar essa notificação?')) {
      deleteNotification.mutate(id);
    }
  };

  const handleChangeWithNina = id => {
    const newRows = rows.map(row => {
      if (row.id === id) {
        return { ...row, with_nina: !row.with_nina };
      }
      return row;
    });
    setRows(newRows);
  };
  return (
    <Paper>
      <TableContainer className={classes.tableContainer}>
        <Table className={classes.table}>
          <TableHead className={classes.stickyHeader}>
            <TableRow>
              <TableCell align='center'>Id</TableCell>
              <TableCell align='center'>Data de criação</TableCell>
              <TableCell align='center'>Língua</TableCell>
              <TableCell align='center'>Título</TableCell>
              <TableCell align='center'>Mensagem</TableCell>
              <TableCell align='center'>Imagem</TableCell>
              <TableCell align='center'>Hora de início</TableCell>
              <TableCell align='center'>Hora de fim</TableCell>
              <TableCell align='center'>Condições</TableCell>
              <TableCell align='center'>Com Nina</TableCell>
              <TableCell align='center'>Status do Envio</TableCell>
              <TableCell align='center'>Qtd de envios</TableCell>
              <TableCell align='center'>Admin</TableCell>
              <TableCell align='center'>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.id}>
                {/* Id */}
                <TableCell align='center'>{row.id}</TableCell>
                {/* Created at */}
                <TableCell align='center'>
                  <div className={classes.cellX9}>{row.created_at}</div>
                </TableCell>
                {/* Locale */}
                <TableCell align='center'>{row.locale}</TableCell>
                {/* Title */}
                <TableCell align='center'>
                  <TextField
                    className={classes.cellX9}
                    defaultValue={row.title}
                    multiline
                    onBlur={e => handleEdit(row.id, 'title', e.target.value)}
                  />
                </TableCell>
                {/* Message */}
                <TableCell align='center'>
                  <TextField
                    className={classes.cellX20}
                    defaultValue={row.message}
                    multiline
                    onBlur={e => handleEdit(row.id, 'message', e.target.value)}
                  />
                </TableCell>
                {/* Image Url */}
                <TableCell align='center'>
                  <TextField
                    className={classes.cellX20}
                    defaultValue={row.image_url}
                    onBlur={e => handleEdit(row.id, 'image_url', e.target.value)}
                  />
                </TableCell>
                {/* Start time */}
                <TableCell align='center'>
                  <TextField
                    type='datetime-local'
                    defaultValue={row.start_time}
                    onBlur={e => handleEdit(row.id, 'start_time', e.target.value)}
                  />
                </TableCell>
                {/* End time */}
                <TableCell align='center'>
                  <div className={classes.cellX9}>{row.end_time}</div>
                </TableCell>
                {/* Conditions */}
                <TableCell align='center'>
                  <div className={classes.conditions}>{row.conditions}</div>
                </TableCell>
                {/* With Nina */}
                <TableCell align='center'>
                  <Checkbox
                    checked={row.with_nina}
                    className={classes.cellX5}
                    onChange={() => handleChangeWithNina(row.id)}
                  />
                </TableCell>
                {/* Status */}
                <TableCell align='center'>{row.status}</TableCell>
                {/* Number of notifications sent */}
                <TableCell align='center'>{row.number_sent}</TableCell>
                {/* Admin */}
                <TableCell align='center'>{row.admin}</TableCell>
                {/* Actions */}
                <TableCell align='center'>
                  <Send className={classes.button} onClick={() => handleSubmit(row.id)}>
                    Update
                  </Send>
                  <StopCircle className={classes.button} onClick={() => handleStop(row.id)}>
                    Stop
                  </StopCircle>
                  <DeleteOutlined className={classes.button} fontSize='large' onClick={() => handleDelete(row.id)}>
                    Delete
                  </DeleteOutlined>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10]}
        component='div'
        count={totalRows}
        onPageChange={(_e, newPage) => setPage(newPage)}
        page={page}
        rowsPerPage={rowsPerPage}
        showFirstButton
      />
    </Paper>
  );
}
